import React, { createContext, useContext, useEffect, useState } from "react";
import { collection, query, onSnapshot, where, doc, setDoc, getDoc, deleteDoc, orderBy } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import { getAuth } from "firebase/auth";
import { db, storage } from "./firebaseConfig";

export interface NoticeType {
    id: string;
    title: string;
    content: string;
    timestamp: number;
    partnerId: string;
    assetPaths?: string[];
}

type EditorAsset = { tempUrl: string; file: File; type: "img" | "video" };

interface NoticeContextType {
    notices: NoticeType[];
    selectedPartnerId: string | null;
    setSelectedPartnerId: (partnerId: string | null) => void;
}

const NoticeContext = createContext<NoticeContextType | null>(null);

export const NoticeProvider: React.FC<React.PropsWithChildren<{}>> = ({ children }) => {
    const [notices, setNotices] = useState<NoticeType[]>([]);
    const [selectedPartnerId, setSelectedPartnerId] = useState<string | null>(null);

    useEffect(() => {
        if (!selectedPartnerId) {
            setNotices([]);
            return;
        }

        const baseRef = collection(db, "notice");
        const targetQuery = query(baseRef, where("partnerId", "==", selectedPartnerId), orderBy("timestamp", "desc"));

        const unsubscribe = onSnapshot(targetQuery, (snapshot) => {
            const list = snapshot.docs
                .map((doc) => ({ ...(doc.data() as NoticeType), id: doc.id }))
                .sort((a, b) => b.timestamp - a.timestamp);
            setNotices(list);
        });

        return () => unsubscribe();
    }, [selectedPartnerId]);

    return (
        <NoticeContext.Provider value={{ notices, selectedPartnerId, setSelectedPartnerId }}>
            {children}
        </NoticeContext.Provider>
    );
};

export const useNotice = (): NoticeContextType => {
    const context = useContext(NoticeContext);
    if (!context) {
        throw new Error("useNotice must be used within a NoticeProvider");
    }
    return context;
};

// 파일 검증 유틸
const validateFile = (file: File, type: "img" | "video") => {
    const maxSize = type === "img" ? 50 * 1024 * 1024 : 200 * 1024 * 1024; // 50MB/200MB
    const allowedTypes =
        type === "img"
            ? ["image/jpeg", "image/png", "image/gif", "image/webp"]
            : ["video/mp4", "video/webm", "video/mov"];

    if (file.size > maxSize) {
        throw new Error(`파일 크기가 너무 큽니다. (최대 ${type === "img" ? "50MB" : "200MB"})`);
    }
    if (!allowedTypes.includes(file.type)) {
        throw new Error("지원하지 않는 파일 형식입니다.");
    }
};

// HTML sanitize 유틸
const sanitizeHtml = (html: string) => {
    return html
        .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, "")
        .replace(/javascript:/gi, "")
        .replace(/on(?!load\b)\w+="[^"]*"/gi, ""); // controls, onload 제외하고 이벤트 핸들러만 제거
};

// Video 태그 속성 변환 유틸 (저장 시점에 필수 속성 보장)
const transformVideoAttributes = (html: string): string => {
    if (!html || typeof html !== "string") return html;

    // DOM 파싱을 위한 임시 컨테이너 생성
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = html;

    // 모든 video 태그 찾기
    const videos = tempDiv.querySelectorAll("video");

    videos.forEach((video) => {
        // 필수 속성들 보장 (멱등성: 이미 있으면 유지)
        if (!video.hasAttribute("controls")) {
            video.setAttribute("controls", "true");
        }
        if (!video.hasAttribute("playsinline")) {
            video.setAttribute("playsinline", "true");
        }
        if (!video.hasAttribute("webkit-playsinline")) {
            video.setAttribute("webkit-playsinline", "true");
        }
        if (!video.hasAttribute("preload")) {
            video.setAttribute("preload", "metadata");
        }

        // 스타일에서 pointer-events 보장
        const currentStyle = video.getAttribute("style") || "";
        if (currentStyle.includes("pointer-events: none")) {
            // pointer-events: none 제거
            const newStyle = currentStyle.replace(/pointer-events\s*:\s*none\s*;?/gi, "");
            video.setAttribute(
                "style",
                newStyle + (newStyle && !newStyle.endsWith(";") ? "; " : "") + "pointer-events: auto;"
            );
        } else if (!currentStyle.includes("pointer-events")) {
            // pointer-events: auto 추가
            video.setAttribute(
                "style",
                currentStyle + (currentStyle && !currentStyle.endsWith(";") ? "; " : "") + "pointer-events: auto;"
            );
        }

        // maxWidth 스타일 보장
        const style = video.getAttribute("style") || "";
        if (!style.includes("max-width")) {
            video.setAttribute("style", style + (style && !style.endsWith(";") ? "; " : "") + "max-width: 100%;");
        }

        // controlsList 제거 (컨트롤 숨김 방지)
        if (video.hasAttribute("controlsList")) {
            video.removeAttribute("controlsList");
        }

        // source 태그가 있으면 type 확인
        const sources = video.querySelectorAll("source");
        sources.forEach((source) => {
            if (!source.hasAttribute("type") && source.getAttribute("src")?.toLowerCase().endsWith(".mp4")) {
                source.setAttribute("type", "video/mp4");
            }
        });
    });

    return tempDiv.innerHTML;
};

export const useNoticeMutations = () => {
    const { selectedPartnerId } = useNotice();

    const createNotice = async (title: string, content: string, assets: EditorAsset[]) => {
        if (!selectedPartnerId) throw new Error("파트너를 선택해주세요.");
        if (!title.trim()) throw new Error("제목을 입력해주세요.");
        if (!content.trim()) throw new Error("내용을 입력해주세요.");

        const auth = getAuth();
        const user = auth.currentUser;
        if (!user) throw new Error("로그인 상태가 아닙니다.");

        const uid = user.uid;
        let uploadedAssets: { tempUrl: string; finalUrl: string; path: string }[] = [];

        try {
            // 1) 파일 검증 및 업로드
            const uploads = await Promise.all(
                assets.map(async (asset, idx) => {
                    validateFile(asset.file, asset.type);

                    const safeName =
                        asset.file.name?.replace(/[^a-zA-Z0-9._-]/g, "_") ||
                        (asset.type === "video" ? `video_${idx}.mp4` : `image_${idx}.png`);
                    const fileName = `${Date.now()}_${idx}_${safeName}`;
                    const path = `notice/${uid}/${fileName}`;
                    const storageRef = ref(storage, path);

                    await uploadBytes(storageRef, asset.file);
                    const url = await getDownloadURL(storageRef);
                    return { tempUrl: asset.tempUrl, finalUrl: url, path };
                })
            );
            uploadedAssets = uploads;

            // 2) tempUrl → 실제 URL 치환
            let finalContent = content;
            uploads.forEach(({ tempUrl, finalUrl }) => {
                finalContent = finalContent.split(tempUrl).join(finalUrl);
            });

            // 3) HTML sanitize 및 video 속성 변환
            const sanitizedContent = sanitizeHtml(finalContent);
            const transformedContent = transformVideoAttributes(sanitizedContent);

            // 4) 문서 생성
            const colRef = collection(db, "notice");
            const docRef = doc(colRef);
            const docId = docRef.id;

            await setDoc(docRef, {
                id: docId,
                partnerId: selectedPartnerId,
                uid,
                title: title.trim(),
                content: transformedContent,
                assetPaths: uploads.map((u) => u.path),
                timestamp: Date.now(),
            });

            return { success: true, docId };
        } catch (error) {
            // 실패 시 업로드된 파일들 정리
            await Promise.all(uploadedAssets.map(({ path }) => deleteObject(ref(storage, path)).catch(() => {})));
            throw error;
        }
    };

    const deleteNoticeWithConfirm = async (noticeId: string) => {
        if (!window.confirm("정말 삭제하시겠습니까?")) return false;

        try {
            const docRef = doc(db, "notice", noticeId);
            const snap = await getDoc(docRef);

            if (!snap.exists()) throw new Error("공지사항을 찾을 수 없습니다.");

            const data = snap.data() as any;
            const assetPaths: string[] = Array.isArray(data?.assetPaths) ? data.assetPaths : [];

            // 에셋 삭제 (실패해도 전체 흐름 막지 않음)
            await Promise.all(assetPaths.map((p) => deleteObject(ref(storage, p)).catch(() => {})));

            await deleteDoc(docRef);
            return { success: true };
        } catch (error) {
            throw error;
        }
    };

    return { createNotice, deleteNoticeWithConfirm, sanitizeHtml, transformVideoAttributes };
};
